import React, { SyntheticEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ssoDomainLookup from '../../api/sso';
import BackButton from '../../components/BackButton';
import Input from '../../components/Input';
import PrimaryButton from '../../components/PrimaryButton';
import ViewWrapper, { FormWrapper } from '../../components/ViewWrapper';
import { EMAIL_REGEX, ssoRedirectUri } from '../../constants';
import logo from '../../img/Airthings_Favicon.png';
import { ErrorType, Invite, SsoAuthorizationState } from '../../models/commonModels';
import { Store } from '../../reducers';
import InviteInfo from '../invite/InviteInfo';
import styles from './SingleSignOnView.module.scss';

type Props = {
    invite?: Invite;
};

const SingleSignOnView = ({ invite }: Props): JSX.Element => {
    const { t: txt } = useTranslation();
    const [email, setEmail] = useState(invite?.email || '');
    const [validateInput, setValidateInput] = useState(false);
    const [invalidDomain, setInvalidDomain] = useState(false);
    const [lookupLoading, setLookupLoading] = useState<boolean>(false);
    const [lookupError, setLookupError] = useState<ErrorType | undefined>(undefined);

    const createSsoState = (clientId: string, federatedClientId: string): string => {
        const searchQuery = window.location.search;
        const state: SsoAuthorizationState = {
            federatedClientId: federatedClientId,
            clientId: clientId,
            urlQueryParams: searchQuery,
        };
        return window.btoa(JSON.stringify(state));
    };

    const createAuthorizationUrl = (
        state: string,
        federatedClientId: string,
        ssoUri: string,
        scope: string
    ): string => {
        if (ssoUri.includes('google')) {
            return (
                ssoUri +
                '?response_type=code' +
                `&scope=${encodeURI(scope)}` +
                `&client_id=${federatedClientId}` +
                `&redirect_uri=${window.location.origin}${ssoRedirectUri}` +
                `&state=${state}` +
                '&access_type=offline'
            );
        } else {
            return (
                ssoUri +
                '?response_type=code' +
                `&scope=${encodeURI(scope)}` +
                `&client_id=${federatedClientId}` +
                `&redirect_uri=${window.location.origin}${ssoRedirectUri}` +
                `&state=${state}`
            );
        }
    };

    const redirectToAuthorizationEndpoint = async (
        ssoUri: string,
        clientId: string,
        federatedClientId: string,
        scope: string
    ): Promise<void> => {
        const state = createSsoState(clientId, federatedClientId);
        const url = createAuthorizationUrl(state, federatedClientId, ssoUri, scope);
        window.location.assign(url);
    };

    const federatedLogin = useCallback(
        async (event: SyntheticEvent<HTMLFormElement> | SyntheticEvent<HTMLButtonElement>) => {
            event.preventDefault();
            setLookupLoading(true);
            const validEmail = EMAIL_REGEX.test(email);
            if (validEmail) {
                const emailDomain = email.split('@')[1];
                await ssoDomainLookup({ emailDomain })
                    .then(response => {
                        if (
                            response.clientId !== undefined &&
                            response.federatedClientId !== undefined &&
                            response.ssoUri !== undefined &&
                            response.scope !== undefined
                        ) {
                            redirectToAuthorizationEndpoint(
                                response.ssoUri,
                                response.clientId,
                                response.federatedClientId,
                                response.scope
                            );
                        } else {
                            setInvalidDomain(true);
                        }
                    })
                    .catch(error => {
                        setLookupError(error);
                        setInvalidDomain(true);
                    });
            } else {
                setValidateInput(false);
            }
            setLookupLoading(false);
        },
        [email]
    );

    const onChange = (value: string): void => {
        setEmail(value.trim());
    };

    return (
        <ViewWrapper>
            <FormWrapper onSubmit={federatedLogin} testAttr="sso-login-form">
                {invite ? (
                    <InviteInfo invite={invite} userRegistered ssoUser />
                ) : (
                    <>
                        <img src={logo} className={styles.logo} alt={txt('Logo.AirthingsLogo')} />
                        <BackButton headerText={txt('SingleSignOn.SsoSignIn')} />
                        <Input
                            label="User.Email"
                            required
                            id="email"
                            type="email"
                            helpText={invalidDomain ? 'SingleSignOn.InvalidDomain' : ''}
                            autoComplete="email"
                            value={email}
                            invalid={!!lookupError || (validateInput && email.length === 0) || invalidDomain}
                            onChange={onChange}
                        />
                    </>
                )}
                <PrimaryButton
                    onClick={federatedLogin}
                    type="submit"
                    loading={lookupLoading}
                    title="Login.Login"
                    testAttr="sso-login-button"
                />
            </FormWrapper>
        </ViewWrapper>
    );
};

const mapStateToProps = (state: Store): Props => {
    const {
        invite: { invite },
    } = state;

    return {
        invite,
    };
};

export default connect(mapStateToProps)(SingleSignOnView);
